import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCompare } from '../../contexts/CompareContext';
import { Button } from '../ui/button';
import { X, GitCompare, User, Trash2 } from 'lucide-react';

// Routes where the CompareBar should be visible
const COMPARE_ENABLED_ROUTES = [
  '/employer/search',
  '/employer/pipeline',
  '/employer/saved',
  '/employer/compare',
  '/employer/talent'
];

const CompareBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCandidates, removeCandidate, clearAll } = useCompare();

  // Check if current route should show the compare bar
  const shouldShowBar = COMPARE_ENABLED_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );

  // Auto-clear state when navigating away from comparison-relevant routes
  useEffect(() => {
    if (!shouldShowBar && selectedCandidates.length > 0) {
      // Clear after a short delay to allow for route transitions
      const timeout = setTimeout(() => {
        clearAll();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [shouldShowBar, selectedCandidates.length, clearAll]);

  // Don't render if no candidates or not on relevant route
  if (selectedCandidates.length === 0 || !shouldShowBar) {
    return null;
  }

  const handleCompare = () => {
    const ids = selectedCandidates.map(c => c.employee_id).join(',');
    navigate(`/employer/compare?ids=${ids}`);
  };

  return (
    <div 
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300 w-[calc(100%-2rem)] max-w-3xl mx-auto px-2 sm:px-0"
      data-testid="compare-bar"
    >
      <div className="bg-[#1E212C] border border-[rgba(255,255,255,0.12)] rounded-xl sm:rounded-2xl shadow-2xl shadow-black/50 p-2 sm:p-3">
        {/* Mobile: Stacked layout, Desktop: Horizontal */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          {/* Selected Candidates */}
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
            {selectedCandidates.map((candidate) => (
              <div 
                key={candidate.employee_id}
                className="relative group flex-shrink-0"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 bg-[#2A2D36] rounded-lg sm:rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 border border-[rgba(255,255,255,0.07)]">
                  {candidate.photo_url ? (
                    <img 
                      src={candidate.photo_url} 
                      alt="" 
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-md sm:rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-[#3A3D48] flex items-center justify-center">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 text-[#707080]" />
                    </div>
                  )}
                  <div className="max-w-[60px] sm:max-w-[100px]">
                    <p className="text-white text-xs sm:text-sm font-medium truncate">{candidate.name}</p>
                    <p className="text-[#707080] text-[8px] sm:text-[10px] truncate hidden sm:block">{candidate.job_title}</p>
                  </div>
                  <button
                    onClick={() => removeCandidate(candidate.employee_id)}
                    className="p-0.5 sm:p-1 rounded-full hover:bg-[#3A3D48] text-[#707080] hover:text-white transition-colors"
                    data-testid={`remove-from-compare-${candidate.employee_id}`}
                  >
                    <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            
            {/* Empty slots - hidden on mobile when we have selections */}
            {selectedCandidates.length < 3 && (
              <div 
                className="hidden sm:flex w-[120px] h-[44px] rounded-xl border-2 border-dashed border-[#3A3D48] items-center justify-center flex-shrink-0"
              >
                <span className="text-[#707080] text-xs">Add more</span>
              </div>
            )}
          </div>

          {/* Divider - hidden on mobile */}
          <div className="hidden sm:block w-px h-10 bg-[rgba(255,255,255,0.12)]" />

          {/* Actions */}
          <div className="flex items-center justify-between sm:justify-start gap-2 pt-1 sm:pt-0 border-t sm:border-t-0 border-[rgba(255,255,255,0.07)]">
            <div className="flex items-center gap-1 text-[10px] sm:hidden font-mono text-[#707080] uppercase">
              {selectedCandidates.length}/3 selected
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleCompare}
                disabled={selectedCandidates.length < 2}
                className="bg-[#E8505A] hover:bg-[#C43840] text-white rounded-lg sm:rounded-xl px-3 sm:px-4 py-1.5 sm:py-2 font-mono text-[10px] sm:text-xs tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="compare-bar-compare-btn"
              >
                <GitCompare className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                Compare ({selectedCandidates.length})
              </Button>
              <Button
                onClick={clearAll}
                variant="ghost"
                className="text-[#707080] hover:text-white hover:bg-[#2A2D36] rounded-lg sm:rounded-xl px-2 sm:px-3 py-1.5 sm:py-2"
                data-testid="compare-bar-clear-btn"
              >
                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Helper text - desktop only */}
      <p className="hidden sm:block text-center text-[10px] font-mono text-[#707080] mt-2 uppercase tracking-wider">
        {selectedCandidates.length < 2 
          ? `Select ${2 - selectedCandidates.length} more to compare`
          : 'Ready to compare'
        }
      </p>
    </div>
  );
};

export default CompareBar;
